import Specialty, {
  Logbook,
  LogbookComponent,
  LogbookComponentConditions,
  Component,
  Option,
  Internship,
  FamilyMedicine,
  InternalMedicine,
  TreeDataDictionary,
  OptionDiagnosisPathology,
  OrganSystem2Pathology
} from '../models/Specialty'
import { Oncology, ICDOCode } from '../models/Oncology'
import { Anesthesiology } from '../models/Anesthesiology'
import {
  GeneralSurgery,
  SurgeryDiagnosis,
  SurgeryProcedure,
  Malignancy,
  DiagnosisQualifiers
} from '../models/GeneralSurgery'
import {
  Orthopedics,
  AnatomicRegion,
  OrthopedicDiagnosis,
  OrthopedicProcedure,
  TraumaDiagnosis,
  TraumaProcedure
} from '../models/Orthopedics'
import { ICPC, ICPCChapter, ICPCComponent } from '../models/FamilyMedicine'
import { DrugClass, DrugTree } from '../models/Drugs'
import Neuroradiology from '../models/Neuroradiology'
import { ICD10Code, Rheumatology } from '../models/Rheumatology'
import { NeurosurgeryProcedure } from '../models/Neurosurgery'

const cache = new Map()

export default {
  methods: {
    fetchSpecialty(s, { seedInternships } = { seedInternships: true }) {
      let specialty = s ? s : this.$store.getters.specialty

      if (cache.has(specialty)) {
        this.seedModels(cache.get(specialty), seedInternships)
        this.seedSpecialty(cache.get(specialty), specialty)
        return Promise.resolve()
      }

      const query = process.env.NODE_ENV === 'development' ? '?env=dev' : ''
      return fetch(
        `${process.env.VUE_APP_API_URL}/specialties/${specialty}${query}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }
      )
        .then(res => {
          if (res.ok) {
            return res.json()
          } else {
            throw Error('could not fetch logbook')
          }
        })
        .then(res => {
          cache.set(specialty, res)
          this.seedModels(res, seedInternships)
          this.seedSpecialty(res, specialty)
        })
        .catch(err => {
          this.$store.dispatch(
            'showErrorSnackbar',
            err.message || 'Something went wrong'
          )
        })
    },
    fetchSpecialtyIfEmpty(s, { seedInternships } = { seedInternships: true }) {
      if (!Logbook.query().first())
        return this.fetchSpecialty(s, seedInternships)
      else return Promise.resolve()
    },
    seedModels(res, seedInternships = true) {
      Option.create({ data: res.options })
      Component.create({
        data: res.components.map(c => ({ ...c, uid: 'specialty.' + c.id }))
      })
      if (seedInternships) {
        Internship.create({ data: res.internships || [] })
      }

      Logbook.create({ data: res.logbooks || [] })
      LogbookComponent.create({ data: res.logbookComponents || [] })

      if (res.conditions) {
        LogbookComponentConditions.create({ data: res.conditions })
      }

      if (res.icdoCodes) {
        ICDOCode.create({ data: res.icdoCodes })
      }
      if (res.icd10Codes) {
        ICD10Code.create({ data: res.icd10Codes })
      }

      if (res.treesDDic) {
        TreeDataDictionary.create({ data: res.treesDDic })
      }
    },
    seedSpecialty(res, specialty) {
      if (specialty === 'anesthesiology') {
        Specialty.registerSpecialty(specialty, Anesthesiology)
        Anesthesiology.create({
          data: {
            name: 'Anesthesiologia',
            id: 'anesthesiology',
            locoRegionalTree: res.locoRegionalTree
          }
        })
      } else if (specialty === 'pathology') {
        OptionDiagnosisPathology.create({
          data: res.options.filter(o => o.component === 'diagnosis')
        })

        OrganSystem2Pathology.create({
          data: res.options.filter(o => o.component === 'organ-system-ii')
        })
      } else if (specialty === 'general-surgery') {
        Specialty.registerSpecialty(specialty, GeneralSurgery)
        GeneralSurgery.create({
          data: {
            name: 'General Surgery',
            id: 'general-surgery',
            organTree: res.organTree
          }
        })
        Malignancy.create({ data: res.malignancy })
        DiagnosisQualifiers.create({ data: res.qualifiers })
        SurgeryDiagnosis.create({ data: res.diagnosisTree })
        SurgeryProcedure.create({ data: res.proceduresTree })
      } else if (specialty === 'family-medicine') {
        FamilyMedicine.create({
          data: {
            name: 'Family Medicine',
            id: 'family-medicine'
          }
        })
        ICPC.create({ data: res.icpc })
        ICPCChapter.create({ data: res.icpcChapters })
        ICPCComponent.create({ data: res.icpcComponents })
        DrugClass.create({ data: res.drugClasses })
        DrugTree.create({
          data: { id: 'main', tree: res.drugTree }
        })
      } else if (specialty === 'neuroradiology') {
        Specialty.registerSpecialty(specialty, Neuroradiology)
        Neuroradiology.create({
          data: {
            name: 'Neuroradiology',
            id: 'neuroradiology'
          }
        })
      } else if (specialty === 'orthopedics') {
        Specialty.registerSpecialty(specialty, Orthopedics)
        Orthopedics.database().register(Orthopedics)

        Orthopedics.create({
          data: {
            name: 'Orthopedics',
            id: 'orthopedics'
          }
        })

        AnatomicRegion.create({ data: res.anatomicRegions })
        OrthopedicDiagnosis.create({ data: res.orthopedicsDiagnosis })
        OrthopedicProcedure.create({ data: res.orthopedicsProcedures })
        TraumaDiagnosis.create({ data: res.traumaDiagnosis })
        TraumaProcedure.create({ data: res.traumaProcedures })
      } else if (specialty === 'oncology') {
        Specialty.registerSpecialty(specialty, Oncology)
        Oncology.database().register(Oncology)
        Oncology.create({
          data: {
            name: 'Oncology',
            id: 'oncology'
          }
        })
      } else if (specialty === 'dermatology') {
        Specialty.create({
          data: {
            name: 'Dermatology',
            id: 'dermatology'
          }
        })
      } else if (specialty === 'internal-medicine') {
        Specialty.registerSpecialty(specialty, InternalMedicine)
        InternalMedicine.database().register(InternalMedicine)
        Specialty.create({
          data: {
            name: 'InternalMedicine',
            id: 'internal-medicine'
          }
        })
      } else if (specialty === 'rheumatology') {
        Specialty.registerSpecialty(specialty, Rheumatology)
        Rheumatology.database().register(Rheumatology)
        Rheumatology.create({
          data: {
            name: 'Rheumatology',
            id: 'rheumatology'
          }
        })
      } else if (specialty === 'neurosurgery') {
        NeurosurgeryProcedure.create({ data: res.procedure })
      }
    }
  }
}
